import React, { useEffect, useRef, useState } from 'react';
import Spline from '@splinetool/react-spline';
import { artistInfo } from '../mock';
import { ArrowRight, Ticket } from 'lucide-react';

class SplineErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <div className="w-full h-full bg-gradient-to-br from-[var(--brand-turquoise)]/10 via-transparent to-transparent" />;
    }
    return this.props.children;
  }
}

const HeroSection = () => {
  const splineContainerRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const [canInitSpline, setCanInitSpline] = useState(false);
  const [splineFailed, setSplineFailed] = useState(false);
  const [webglAvailable, setWebglAvailable] = useState(true);

  const checkWebGL = () => {
    if (typeof document === 'undefined') return false;
    try {
      const canvas = document.createElement('canvas');
      return !!(
        canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl')
      );
    } catch {
      return false;
    }
  };

  // Ensure we never attempt to initialize Spline before mount (and avoid SSR edge cases).
  useEffect(() => {
    setIsMounted(true);
    setWebglAvailable(checkWebGL());
  }, []);

  // Only render Spline once its container has a non-zero size.
  // This prevents initialization during 0x0 layout states which can cascade into canvas errors.
  useEffect(() => {
    if (!isMounted) return;
    if (splineFailed || !webglAvailable) return;

    let rafId = 0;
    const start = performance.now();
    const timeoutMs = 4000;

    const checkSize = () => {
      const el = splineContainerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setCanInitSpline(true);
          return;
        }
      }

      // Give up after a short time and show fallback (prevents infinite loops).
      if (performance.now() - start > timeoutMs) {
        setSplineFailed(true);
        return;
      }

      rafId = window.requestAnimationFrame(checkSize);
    };

    rafId = window.requestAnimationFrame(checkSize);
    return () => window.cancelAnimationFrame(rafId);
  }, [isMounted, splineFailed, webglAvailable]);

  const scrollToContact = () => {
    try {
      const element = document.querySelector('#contact');
      if (element) {
        const offset = 20;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
      }
    }
  };

  return (
    <section className="relative min-h-screen bg-black flex items-center overflow-hidden" id="hero">
      {/* Grid Overlay */}
      <div className="absolute inset-0 grid-overlay opacity-10" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-[7.6923%] w-full relative">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Left Side - Content */}
          <div className="space-y-6 md:space-y-8 animate-slide-in pt-24 md:pt-24 lg:pt-28 order-2 md:order-1 relative z-30 w-full overflow-hidden">
            <div className="space-y-4 w-full">
              <h1 id="hero-main-title" className="hero-main-title leading-tight w-full">
                <span id="hero-title-line1" className="block hero-title-text w-full">DJ & Producer</span>
                <span id="hero-title-location" className="block hero-title-location mt-2 w-full">Kelowna, BC</span>
              </h1>
              <p id="hero-description" className="hero-description body-large max-w-xl leading-relaxed">
                Specializing in dubstep, bass, and versatile electronic sets. Available for bookings at clubs, festivals, and events across Canada.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <a 
                href="#booking"
                onClick={(e) => {
                  e.preventDefault();
                  try {
                    const element = document.querySelector('#booking');
                    if (element) {
                      const offset = 20;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - offset;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    } else {
                    }
                  } catch (error) {
                  }
                }}
                className="btn-primary group w-full sm:w-auto inline-flex justify-center"
              >
                <Ticket size={18} className="text-black flex-shrink-0" />
                <span className="text-black">Book</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform text-black flex-shrink-0" size={18} />
              </a>
              <a 
                id="hero-soundcloud-link"
                href="https://on.soundcloud.com/p7lR72fb7GYKSPuCUZ" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-secondary w-full sm:w-auto inline-flex justify-center"
              >
                <span id="hero-soundcloud-text" className="text-white hero-link-text hero-link-text-visible">Listen on SoundCloud</span>
              </a>
            </div>

            {/* Genre Tags */}
            <div className="flex justify-center gap-3 pt-8 border-t border-white/25 flex-wrap">
              {artistInfo.genres.map((genre, i) => (
                <span 
                  key={i} 
                  id={`hero-genre-tag-${i}`}
                  className="hero-genre-tag"
                >
                  {genre}
                </span>
              ))}
            </div>
          </div>

          {/* Right Side - 3D Spline */}
          <div className="relative h-[400px] md:h-[500px] lg:h-[600px] w-full order-1 md:order-2 flex items-center justify-center overflow-hidden z-10">
            <div className="relative w-full h-full max-w-[500px] mx-auto">
              <div
                ref={splineContainerRef}
                className="relative w-full h-full animate-pulse-glow hero-spline-container"
              >
                {/* Render Spline only after mount + non-zero container size */}
                {isMounted && webglAvailable && canInitSpline && !splineFailed ? (
                  <SplineErrorBoundary>
                    <Spline
                      scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
                      className="w-full h-full hero-spline-full"
                      onError={() => {
                        // Graceful fallback if Spline fails (no console spam)
                        setSplineFailed(true);
                      }}
                      onLoad={() => {
                        // no-op: Spline loaded successfully
                      }}
                    />
                  </SplineErrorBoundary>
                ) : (
                  // Graceful fallback that preserves layout without visual jank
                  <div className="w-full h-full bg-gradient-to-br from-[var(--brand-turquoise)]/10 via-transparent to-transparent" />
                )}
              </div>
              {/* Glow effect overlay - pointer-events-none so it doesn't block cursor */}
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-turquoise)]/20 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
};

export default HeroSection;